import { createImageVariantConfig } from '../../util/sdkLoader';
import { isErrorUserPendingApproval, isForbiddenError, storableError } from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import {
  parseDateFromISO8601,
  getExclusiveEndDate,
  addTime,
  subtractTime,
  daysBetween,
  getStartOf,
} from '../../util/dates';
import { constructQueryParamName, isOriginInUse, isStockInUse } from '../../util/search';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { parse } from '../../util/urlHelpers';
import { isEventDateInRange, parseEventDates } from '../../util/eventDateHelpers';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { SHOP_LISTING_TYPES } from '../../extensions/shop/config';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 100;

// The Marketplace API caps a single listings query at 100 results per page.
// The results list stays paginated (RESULT_PAGE_SIZE per page), but the MAP
// should show *every* matching listing as a pin. To do that we fetch all
// result pages and merge them into a separate "map listings" set.
// This cap is a safety backstop so an unexpectedly huge result set can't
// trigger a runaway number of requests (10 pages * 100 = up to 1000 pins).
const MAX_MAP_LISTINGS_PAGES = 10;

// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';

export const SEARCH_MAP_LISTINGS_REQUEST =
  'app/SearchPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS =
  'app/SearchPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR =
  'app/SearchPage/SEARCH_MAP_LISTINGS_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING =
  'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

export const SET_FIRST_TIME_OPEN_PAGE = 'app/SearchPage/SET_FIRST_TIME_OPEN_PAGE';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  // IDs of every matching listing across all result pages, used for map pins.
  searchMapListingIds: [],
  searchMapListingsError: null,
  isFirstTimeOpenPage: true,
};

const resultIds = data => {
  const listings = data.data;
  return listings
    .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
    .map(l => l.id);
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
      };
    case SEARCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        searchInProgress: false,
        searchListingsError: payload,
      };

    case SEARCH_MAP_LISTINGS_REQUEST:
      return { ...state, searchMapListingsError: null };
    case SEARCH_MAP_LISTINGS_SUCCESS:
      return { ...state, searchMapListingIds: payload.listingIds };
    case SEARCH_MAP_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchMapListingsError: payload };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };
    
    case SET_FIRST_TIME_OPEN_PAGE:
      return {
        ...state,
        isFirstTimeOpenPage: payload,
      };
    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = response => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const searchMapListingsRequest = () => ({
  type: SEARCH_MAP_LISTINGS_REQUEST,
});

export const searchMapListingsSuccess = listingIds => ({
  type: SEARCH_MAP_LISTINGS_SUCCESS,
  payload: { listingIds },
});

export const searchMapListingsError = e => ({
  type: SEARCH_MAP_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const setFirstTimeOpenPage = payload => ({
  type: SET_FIRST_TIME_OPEN_PAGE,
  payload,
}); 

// Build the params object passed to sdk.listings.query from the SearchPage search params.
// Extracted to module scope so both the paginated results query (searchListings)
// and the all-pages map query (searchMapListings) build identical filter params.
const getListingQueryParams = (searchParams, config) => {
  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = (listingTypes, listingTypePathParam, isListingTypeVariant) => {
    if (isListingTypeVariant) {
      return {
        pub_listingType: listingTypePathParam,
      };
    }

    // Kate's Shop listing types (see extensions/shop/config) are only ever reachable
    // through the dedicated /shop listing-type-variant route, never through the main
    // search grid/map. Always send an allowlist here so they can't leak in, regardless
    // of the (separate, currently unused) config.listing.enforceValidListingType flag.
    const allowedListingTypes = (listingTypes || [])
      .map(l => l.listingType)
      .filter(listingType => !SHOP_LISTING_TYPES.includes(listingType));

    // If config hasn't loaded yet, listingTypes is empty - omit the param rather than
    // sending an empty allowlist, which would match zero listings instead of "everything".
    return allowedListingTypes.length > 0 ? { pub_listingType: allowedListingTypes } : {};
  };

  const omitInvalidCategoryParams = params => {
    const categoryConfig = config.search.defaultFilters?.find(f => f.schemaType === 'category');
    const categories = config.categoryConfiguration.categories;
    const { key: prefix, scope } = categoryConfig || {};
    const categoryParamPrefix = constructQueryParamName(prefix, scope);

    const validURLParamForCategoryData = (prefix, categories, level, params) => {
      const levelKey = `${categoryParamPrefix}${level}`;
      const levelValue =
        typeof params?.[levelKey] !== 'undefined' ? `${params?.[levelKey]}` : undefined;
      const foundCategory = categories.find(cat => cat.id === levelValue);
      const subcategories = foundCategory?.subcategories || [];
      return foundCategory && subcategories.length > 0
        ? {
            [levelKey]: levelValue,
            ...validURLParamForCategoryData(prefix, subcategories, level + 1, params),
          }
        : foundCategory
        ? { [levelKey]: levelValue }
        : {};
    };

    const categoryKeys = validURLParamForCategoryData(prefix, categories, 1, params);
    const nonCategoryKeys = Object.entries(params).reduce(
      (picked, [k, v]) => (k.startsWith(categoryParamPrefix) ? picked : { ...picked, [k]: v }),
      {}
    );

    return { ...nonCategoryKeys, ...categoryKeys };
  };

  const priceSearchParams = priceParam => {
    const inSubunits = value =>
      convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  const datesSearchParams = datesParam => {
    const searchTZ = 'Etc/UTC';
    const datesFilter = config.search.defaultFilters.find(
      f => f.key === 'dates'
    );
    const values = datesParam ? datesParam.split(',') : [];
    const hasValues = datesFilter && datesParam && values.length === 2;
    const { dateRangeMode, availability } = datesFilter || {};
    const isNightlyMode = dateRangeMode === 'night';
    const isEntireRangeAvailable = availability === 'time-full';

    // SearchPage need to use a single time zone but listings can have different time zones
    // We need to expand/prolong the time window (start & end) to cover other time zones too.
    //
    // NOTE: you might want to consider changing UI so that
    //   1) location is always asked first before date range
    //   2) use some 3rd party service to convert location to time zone (IANA tz name)
    //   3) Make exact dates filtering against that specific time zone
    //   This setup would be better for dates filter,
    //   but it enforces a UX where location is always asked first and therefore configurability
    const getProlongedStart = date => subtractTime(date, 14, 'hours', searchTZ);
    const getProlongedEnd = date => addTime(date, 12, 'hours', searchTZ);

    const startDate = hasValues
      ? parseDateFromISO8601(values[0], searchTZ)
      : null;
    const endRaw = hasValues ? parseDateFromISO8601(values[1], searchTZ) : null;
    const endDate =
      hasValues && isNightlyMode
        ? endRaw
        : hasValues
        ? getExclusiveEndDate(endRaw, searchTZ)
        : null;

    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    const dayCount = isEntireRangeAvailable
      ? daysBetween(startDate, endDate)
      : 1;
    const day = 1440;
    const hour = 60;
    // When entire range is required to be available, we count minutes of included date range,
    // but there's a need to subtract one hour due to possibility of daylight saving time.
    // If partial range is needed, then we just make sure that the shortest time unit supported
    // is available within the range.
    // You might want to customize this to match with your time units (e.g. day: 1440 - 60)
    const minDuration = isEntireRangeAvailable ? dayCount * day - hour : hour;
    return hasValidDates
      ? {
          start: getProlongedStart(startDate),
          end: getProlongedEnd(endDate),
          // Availability can be time-full or time-partial.
          // However, due to prolonged time window, we need to use time-partial.
          availability: 'time-partial',
          // minDuration uses minutes
          minDuration,
        }
      : {};
  };

  const stockFilters = datesMaybe => {
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // If dates filter is not in use,
    //   1) Add minStock filter with default value (1)
    //   2) Add relaxed stockMode: "match-undefined"
    // The latter is used to filter out all the listings that explicitly are out of stock,
    // but keeps bookable and inquiry listings.
    return hasDatesFilterInUse
      ? {}
      : { minStock: 1, stockMode: 'match-undefined' };
  };

  const seatsSearchParams = (seats, datesMaybe) => {
    const seatsFilter = config.search.defaultFilters.find(f => f.key === 'seats');
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // Seats filter cannot be applied without dates
    return hasDatesFilterInUse && seatsFilter ? { seats } : {};
  };

  const {
    perPage,
    price,
    dates,
    seats,
    sort,
    mapSearch,
    listingTypePathParam,
    isListingTypeVariant,
    pub_eventDateStart,
    pub_eventDateEnd,
    ...restOfParams
  } = searchParams;
  const priceMaybe = priceSearchParams(price);
  const datesMaybe = datesSearchParams(dates);
  const stockMaybe = stockFilters(datesMaybe);
  const seatsMaybe = seatsSearchParams(seats, datesMaybe);
  const sortMaybe = sort === config.search.sortConfig.relevanceKey ? {} : { sort };

  return {
    // The rest of the params except invalid nested category-related params
    // Note: invalid independent search params are still passed through
    ...omitInvalidCategoryParams(restOfParams),
    // If the search page variant is of type /s/:listingType, this sets the pub_listingType
    // query parameter to the value of the listing type path parameter. The ordering matters here,
    // since this value overrides any possible pub_listingType value coming from query parameters
    // i.e. the previous row.
    //
    // Only one value is currently supported in pub_listingType – if you want to support e.g.
    // /s/:listingType?pub_listingType=[otherListingType] => pub_listingType=listingType,otherListingType,
    // you'll need to customize a logic that merges the query param and path param values.
    ...searchValidListingTypes(
      config.listing.listingTypes,
      listingTypePathParam,
      isListingTypeVariant
    ),
    ...priceMaybe,
    ...datesMaybe,
    ...stockMaybe,
    ...seatsMaybe,
    // Pass event date filters directly - timestamps already formatted by EventDateRangeFilter
    ...(pub_eventDateStart ? { pub_eventDateStart } : {}),
    ...(pub_eventDateEnd ? { pub_eventDateEnd } : {}),
    ...sortMaybe,
    perPage,
  };
};

const parseEventRangeTimestamp = value => {
  if (!value) return null;

  const timestamp = Number.parseInt(String(value).replace(/,/g, ''), 10);
  return Number.isFinite(timestamp) ? timestamp : null;
};

// Client-side post-filtering for event date range:
// 1) keep only listings with event date data
// 2) apply exact match against eventDate array when available
// Returns the response unchanged when no event date filter is active.
const applyEventDateFilter = (responseForFiltering, params) => {
  const hasEventDateFilter = params.pub_eventDateStart || params.pub_eventDateEnd;
  if (!hasEventDateFilter || !responseForFiltering.data?.data) {
    return responseForFiltering;
  }

  const selectedRangeStart = parseEventRangeTimestamp(params.pub_eventDateEnd);
  const selectedRangeEnd = parseEventRangeTimestamp(params.pub_eventDateStart);

  const filteredListings = responseForFiltering.data.data.filter(listing => {
    const { publicData } = listing.attributes || {};
    const eventDateValue = publicData?.eventDatesArray || publicData?.eventDate;
    const parsedDates = parseEventDates(eventDateValue);

    const hasBoundaries =
      Number.isFinite(publicData?.eventDateStart) && Number.isFinite(publicData?.eventDateEnd);
    const hasAnyDateData = parsedDates.length > 0 || hasBoundaries;

    if (!hasAnyDateData) {
      return false;
    }

    const hasValidSelectedRange =
      Number.isFinite(selectedRangeStart) && Number.isFinite(selectedRangeEnd);

    if (!hasValidSelectedRange) {
      return true;
    }

    // Exact check for non-consecutive dates (e.g. weekly recurring events).
    if (parsedDates.length > 0) {
      return isEventDateInRange(eventDateValue, selectedRangeStart, selectedRangeEnd);
    }

    // Fallback for legacy data that has only boundaries.
    return (
      publicData.eventDateStart <= selectedRangeEnd &&
      publicData.eventDateEnd >= selectedRangeStart
    );
  });

  return {
    ...responseForFiltering,
    data: {
      ...responseForFiltering.data,
      data: filteredListings,
      meta: {
        ...responseForFiltering.data.meta,
        totalItems: filteredListings.length,
        totalPages: Math.ceil(filteredListings.length / (params.perPage || 24)),
      },
    },
  };
};

export const searchListings = (searchParams, config) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(searchListingsRequest(searchParams));

  const params = getListingQueryParams(searchParams, config);

  return sdk.listings
    .query(params)
    .then(async response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      const hasEventDateFilter = params.pub_eventDateStart || params.pub_eventDateEnd;

      let responseForFiltering = response;

      // Some legacy listings may have only eventDate/eventDatesArray without eventDateStart/eventDateEnd.
      // In that case API-level range filters can return 0 results even when matching dates exist.
      if (hasEventDateFilter && response.data?.data?.length === 0) {
        const { pub_eventDateStart, pub_eventDateEnd, ...fallbackParams } = params;
        responseForFiltering = await sdk.listings.query(fallbackParams);
      }

      const filteredResponse = applyEventDateFilter(responseForFiltering, params);

      dispatch(addMarketplaceEntities(filteredResponse, sanitizeConfig));
      dispatch(searchListingsSuccess(filteredResponse));
      return filteredResponse;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(searchListingsError(error));
      if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
        throw e;
      }
    });
};

// Fetch every result page for the current search and merge them into a single
// set of listing IDs used for map pins. The results list stays paginated at
// RESULT_PAGE_SIZE, but the map should show all matching listings as pins no
// matter how far it's zoomed out. The Marketplace API caps a query at 100
// results/page, so we page through (up to MAX_MAP_LISTINGS_PAGES) and accumulate.
// Page 1 is fetched first to learn the total page count, then the remaining
// pages are fetched in parallel to keep latency low.
export const searchMapListings = (searchParams, config) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(searchMapListingsRequest());

  const params = getListingQueryParams(searchParams, config);
  const listingFields = config?.listing?.listingFields;
  const sanitizeConfig = { listingFields };

  const handlePage = response => {
    const filteredResponse = applyEventDateFilter(response, params);
    dispatch(addMarketplaceEntities(filteredResponse, sanitizeConfig));
    return resultIds(filteredResponse.data);
  };

  return sdk.listings
    .query({ ...params, page: 1 })
    .then(firstResponse => {
      const firstPageIds = handlePage(firstResponse);
      const totalPages = firstResponse.data?.meta?.totalPages || 1;
      const lastPage = Math.min(totalPages, MAX_MAP_LISTINGS_PAGES);

      if (lastPage <= 1) {
        return firstPageIds;
      }

      const remainingPages = [];
      for (let page = 2; page <= lastPage; page++) {
        remainingPages.push(sdk.listings.query({ ...params, page }).then(handlePage));
      }

      return Promise.all(remainingPages).then(pagesIds =>
        firstPageIds.concat(...pagesIds)
      );
    })
    .then(listingIds => {
      dispatch(searchMapListingsSuccess(listingIds));
      return listingIds;
    })
    .catch(e => {
      // Map pins are supplementary: if this fails, the paginated results (and
      // their pins) still render, so we swallow the error instead of rethrowing.
      dispatch(searchMapListingsError(storableError(e)));
    });
};

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  // In private marketplace mode, this page won't fetch data if the user is unauthorized
  const { listingType: listingTypePathParam } = params || {};
  const state = getState();
  const currentUser = state.user?.currentUser;
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const hasViewingRights = currentUser && hasPermissionToViewData(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData =
    !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized && hasViewingRights);
  if (!canFetchData) {
    return Promise.resolve();
  }

  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const { page = 1, address, origin, sort, ...rest } = queryParams;
  const isSortByDistance = !sort;
  const originMaybe =
    isOriginInUse(config) && origin && isSortByDistance ? { origin } : {};

  const listingTypeVariantMaybe = listingTypePathParam
    ? { listingTypePathParam, isListingTypeVariant: true }
    : {};

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const commonSearchParams = {
    ...rest,
    ...originMaybe,
    ...listingTypeVariantMaybe,
    page,
    ...(!isSortByDistance ? { sort } : {}),
    perPage: RESULT_PAGE_SIZE,
    include: ['author', 'images'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'deleted',
      'state',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.location',
      'publicData.producer_type_category',
      'publicData.what_do_you_produce',
      'publicData.type',
      'publicData.markets_events',
      'publicData.producers_places',
      'publicData.features',
      'publicData.placetype',
      'publicData.farm_to_fork_dining_features',
      'publicData.categoryLevel1',
      'publicData.cardStyle',
      'publicData.eventDate',
      'publicData.eventDateStart',
      'publicData.eventDateEnd',
      // These help rendering of 'purchase' listings,
      // when transitioning from search page to listing page
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
      'publicData.experience_type',
    ],
    'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.publicData.displayName',
      'publicData.priceVariationsEnabled',
      'publicData.priceVariants',
    ],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  };

  // The results list is paginated (RESULT_PAGE_SIZE per page). In the map
  // layout we ALSO fetch every result page and merge them, so the map shows
  // all matching listings as pins regardless of zoom (see searchMapListings).
  // The grid layout has no map, so we skip that extra work there.
  const isMapLayout = config.layout?.searchPage?.variantType === 'map';

  const searchCalls = [dispatch(searchListings(commonSearchParams, config))];
  if (isMapLayout) {
    searchCalls.push(dispatch(searchMapListings(commonSearchParams, config)));
  }

  return Promise.all(searchCalls);
};
